import { FC, useState } from 'react';
import { Popover, Portal } from '@chakra-ui/react';
import { PlusIcon } from '@phosphor-icons/react';
import {
  IBlockBase,
  BlockType,
  ICoachSessionAthlete,
} from '../../endpoints/coachSessions';
import { ISportTaxonomy } from '../../models/trainingStatistics';
import { SequentialBlock } from './blocks/SequentialBlock';
import { ParallelBlock } from './blocks/ParallelBlock';
import { RotationBlock } from './blocks/RotationBlock';
import { BreakBlock } from './blocks/BreakBlock';
import { PhysicalBlock } from './blocks/PhysicalBlock';
import { BLOCK_TYPE_CONFIG } from './blocks/BlockBase';
import styles from './CoachSessionDetail.module.scss';

interface SessionCanvasProps {
  blocks: IBlockBase[];
  onUpdate: (index: number, patch: Partial<IBlockBase>) => void;
  onDelete: (index: number) => void;
  onDuplicate: (index: number) => void;
  onMoveUp: (index: number) => void;
  onMoveDown: (index: number) => void;
  onAdd: (type: BlockType, atIndex?: number) => void;
  sport?: string | null;
  taxonomy?: ISportTaxonomy | null;
  totalBlocks?: number;
  rosterAthletes?: ICoachSessionAthlete[];
}

const BLOCK_TYPES: BlockType[] = [
  'sequential',
  'parallel',
  'rotation',
  'break',
  'physical',
];

// Popover "Aggiungi blocco" via Chakra v3 (Ark UI + Zag.js + Floating UI
// internal). Portal a body — escape canvasCol overflow:auto + ancestors
// overflow:hidden. Auto-flip + shift in viewport, dismiss on outside click,
// ESC, role/ARIA gestiti dalla primitive.
const AddBlockPopover: FC<{
  onSelect: (type: BlockType) => void;
}> = ({ onSelect }) => {
  const [open, setOpen] = useState(false);

  return (
    <Popover.Root
      open={open}
      onOpenChange={(e) => setOpen(e.open)}
      positioning={{
        placement: 'top',
        gutter: 8,
        // fitViewport=false disabilita il size middleware di Floating UI
        // che imposta max-height = available height (troncava le 5 cards)
        fitViewport: false,
      }}
      lazyMount
      unmountOnExit
    >
      <Popover.Trigger asChild>
        <button type="button" className={styles.addBlockBtn}>
          <PlusIcon size={14} weight="bold" />
          Aggiungi blocco
        </button>
      </Popover.Trigger>
      <Portal>
        <Popover.Positioner>
          <Popover.Content
            className={styles.blockTypeMenu}
            style={{
              // override del module SCSS: Chakra positiona in JS, nessun
              // bottom/transform statico necessario.
              // maxHeight/height esplicito a 'auto' perche' il middleware
              // size di Chakra v3 imposta max-height alla available height
              // del viewport-flip, troncando le 5 cards (3 righe da ~80px).
              position: 'absolute',
              bottom: 'auto',
              left: 'auto',
              transform: 'none',
              width: 'min(460px, calc(100vw - 32px))',
              height: 'auto',
              maxHeight: 'none',
              overflow: 'visible',
            }}
          >
            <p className={styles.blockTypeMenuTitle}>Scegli tipologia blocco</p>
            <div className={styles.blockTypeMenuGrid}>
              {BLOCK_TYPES.map((t) => {
                const cfg = BLOCK_TYPE_CONFIG[t];
                return (
                  <button
                    key={t}
                    type="button"
                    className={styles.blockTypeCard}
                    style={
                      {
                        '--card-accent': cfg.color,
                      } as React.CSSProperties
                    }
                    onClick={() => {
                      onSelect(t);
                      setOpen(false);
                    }}
                  >
                    <span
                      className={styles.blockTypeCardIcon}
                      style={{ background: `${cfg.color}1A`, color: cfg.color }}
                    >
                      <cfg.Icon size={20} color={cfg.color} />
                    </span>
                    <span className={styles.blockTypeCardLabel}>{cfg.label}</span>
                    <span className={styles.blockTypeCardDesc}>{cfg.description}</span>
                  </button>
                );
              })}
            </div>
          </Popover.Content>
        </Popover.Positioner>
      </Portal>
    </Popover.Root>
  );
};

function renderBlock(
  block: IBlockBase,
  index: number,
  props: Omit<SessionCanvasProps, 'blocks' | 'onAdd'>,
) {
  const common = {
    block,
    index,
    onChange: (patch: Partial<IBlockBase>) => props.onUpdate(index, patch),
    onDelete: () => props.onDelete(index),
    onDuplicate: () => props.onDuplicate(index),
    onMoveUp: () => props.onMoveUp(index),
    onMoveDown: () => props.onMoveDown(index),
    sport: props.sport ?? null,
    taxonomy: props.taxonomy ?? null,
    totalBlocks: props.totalBlocks,
  };

  switch (block.blockType) {
    case 'sequential':
      return <SequentialBlock key={block._id ?? index} {...common} />;
    case 'parallel':
      return (
        <ParallelBlock
          key={block._id ?? index}
          {...common}
          rosterAthletes={props.rosterAthletes}
        />
      );
    case 'rotation':
      return (
        <RotationBlock
          key={block._id ?? index}
          {...common}
          rosterAthletes={props.rosterAthletes}
        />
      );
    case 'break':
      return <BreakBlock key={block._id ?? index} {...common} />;
    case 'physical':
      return (
        <PhysicalBlock
          key={block._id ?? index}
          {...common}
          rosterAthletes={props.rosterAthletes}
        />
      );
    default:
      return null;
  }
}

export const SessionCanvas: FC<SessionCanvasProps> = ({
  blocks,
  onUpdate,
  onDelete,
  onDuplicate,
  onMoveUp,
  onMoveDown,
  onAdd,
  sport,
  taxonomy,
  totalBlocks,
  rosterAthletes,
}) => {
  return (
    <div className={styles.blockList}>
      {blocks.map((block, idx) => (
        <div key={block._id ?? idx}>
          {/* ─── Spacer + inline add ──────────── */}
          {idx > 0 && (
            <div className={styles.blockSpacer}>
              <div className={styles.blockSpacerLine} />
              <button
                type="button"
                className={styles.addBetweenBtn}
                title="Inserisci blocco qui"
                onClick={() => {
                  // For simplicity opens footer-style dropdown; could be inline
                  onAdd('sequential', idx);
                }}
              >
                <PlusIcon size={10} weight="bold" />
              </button>
              <div className={styles.blockSpacerLine} />
            </div>
          )}
          {renderBlock(block, idx, {
            onUpdate,
            onDelete,
            onDuplicate,
            onMoveUp,
            onMoveDown,
            sport,
            taxonomy,
            totalBlocks: totalBlocks ?? blocks.length,
            rosterAthletes,
          })}
        </div>
      ))}

      {/* ─── Footer add block button ─────────── */}
      <div className={styles.addBlockFooter}>
        <div className={styles.addBlockDropdown}>
          <AddBlockPopover onSelect={(type) => onAdd(type)} />
        </div>
      </div>
    </div>
  );
};
